import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { SshTarget } from '@voice-assistant/shared';

// ════════════════════════════════════════════════════════════════════════════
// SSH Terminal FSM store — Phase 0 (scaffold + TRANSITIONS, NO consumers wired)
//
// Origin-keyed Zustand store for the SSH terminal subsystem, applying the
// workspace Frontend FSM pattern (server-config/docs/frontend-fsm-pattern.md):
//   1. Explicit FSM        — the `mode` union + the exported TRANSITIONS table.
//   2. Origin-keyed store  — Map<terminalId, SshTerminalState> with persist.
//   3. Server-authoritative — reconcile(sshState) is the only truth signal for
//                             a `detached` terminal (no frontend timeout guesses).
//   4. Atomic transitions  — one user intent = one named action (commitConnect,
//                             exitTerminal, reconcile), never scattered setters.
//   5. Setpoint evals      — setpoint-evals/ssh-terminal/ drives THIS store.
//
// Plan: voice-assistant/plans/ssh-terminal-fsm-migration.md (Phase 0).
// Mirrors the structure of sessionLoadStore.ts (Map-keyed, module-scope actions,
// hook selectors) + composerStore.ts (persist + Map serializer).
//
// Phase 0 is a SCAFFOLD: the store + actions + TRANSITIONS exist and are
// transition-faithful (the simulator drives them), but NOTHING reads or writes
// this store in the live app yet. Phase 1 migrates SshTerminal.tsx + the
// useSocket SSH handlers onto it (dual-write); Phase 2 makes the actions atomic
// and promotes the back-arrow band-aid (#1371) into exitTerminal(); Phase 3
// adds the SSH_STATE reconcile path. Until then this module has zero importers.
// ════════════════════════════════════════════════════════════════════════════

// ─── State types ───────────────────────────────────────────────────────────────

/**
 * FSM mode for a single SSH terminal.
 *
 *   idle          no terminal — list/chat view, nothing connected.
 *   picking       target picker open, fetching/showing SSH_TARGETS.
 *   connecting    SSH_CONNECT emitted, awaiting SSH_CONNECTED.
 *   connected     PTY live; SSH_OUTPUT renders, user can type.
 *   detached      socket dropped; PTY kept ALIVE server-side. The client does
 *                 NOT pretend to know the PTY is dead — it waits for the
 *                 server's typed SSH_STATE on reconnect (the stuck-reattach fix).
 *   disconnecting voluntary leave in flight (exitTerminal → SSH_DISCONNECT),
 *                 awaiting SSH_DISCONNECTED.
 *   error         connect failed; awaiting user dismiss.
 *
 * Full transition table is exported as `TRANSITIONS` below (R-A1).
 */
export type SshTerminalMode =
  | 'idle'
  | 'picking'
  | 'connecting'
  | 'connected'
  | 'detached'
  | 'disconnecting'
  | 'error';

export interface SshTerminalState {
  mode: SshTerminalMode;
  /** Server-assigned terminal id (from SSH_CONNECTED / SSH_STATE). Phase 4
   *  formalizes this on the wire; v1 mirrors the Map key. */
  terminalId?: string;
  /** Connected/selected target. Kept across `detached` so reconcile can resume. */
  target?: SshTarget;
  /** PTY output chunks (each SSH_OUTPUT appended). NOT persisted — re-sourced
   *  from the server buffer on reconcile (Phase 3). */
  output: string[];
  /** Last N voice-transcribed commands (SSH mode). Metadata — persisted. */
  voiceHistory: string[];
  /** Error message when mode === 'error'. Cleared on dismiss / next connect. */
  lastError?: string;
  /** Date.now() at SSH_CONNECTED — for staleness / reconcile heuristics. */
  connectedAt?: number;
  /** Bumped on each SSH_OUTPUT / input — last-activity for idle-timeout (server). */
  lastActivityAt?: number;
  /** Highest applied per-terminal output `seq` (Phase 4). appendOutput drops any
   *  chunk with seq <= lastSeq — idempotent dedup across reattach replay + live
   *  chunks. Undefined until the first seq-carrying chunk/reconcile. */
  lastSeq?: number;
  /** Monotonic counter bumped on every output REPLACE/RESET (commitConnect,
   *  reconcile, onDisconnected, dismissError) — NEVER on appendOutput. The
   *  renderer resets xterm when this changes (replace) and writes the delta
   *  otherwise (append), which retires the writtenRef append-only symptom-guard
   *  that mis-rendered P3's reconcile output-replace (Phase 4). */
  outputEpoch: number;
}

export interface SshTerminalStoreShape {
  /** Origin-keyed: one entry per terminal. v1 has a single entry; the Map keeps
   *  the door open for multi-terminal without reshaping the store. */
  terminals: Map<string, SshTerminalState>;
  /** Available SSH targets (SSH_TARGETS payload). Global, ephemeral — NOT
   *  persisted (re-fetched on openTerminal). Drives the picker. */
  targets: SshTarget[];
}

/** Server-authoritative reconcile payload (Phase 3 wire: WsEvent.SSH_STATE). */
export interface SshStateReconcile {
  terminalId: string;
  attached: boolean;
  target?: SshTarget;
  /** Full server PTY output buffer. When present on an attached reconcile it
   *  REPLACES the client's (stale) scrollback — the server is the source of
   *  truth for output across a reconnect. Absent ⇒ output is left untouched. */
  replay?: string;
  /** High-water output `seq` the replay buffer represents — adopted as lastSeq so
   *  live chunks already in the buffer (seq <= this) dedupe on reattach (Phase 4). */
  seq?: number;
}

// ─── Default state factory ──────────────────────────────────────────────────────

function defaultSshTerminalState(): SshTerminalState {
  return { mode: 'idle', output: [], voiceHistory: [], outputEpoch: 0 };
}

const VOICE_HISTORY_CAP = 20;

/**
 * v1 is single-terminal: the whole app shares ONE client-side terminal id, so
 * every consumer (useSocket SSH handlers + the SshTerminal / picker / voice-history
 * components) keys the store by this constant. Distinct from a *target* id (e.g.
 * `'local-0'`) — this identifies the client terminal surface, not the SSH host.
 * Phase 4 formalizes a server-assigned `terminalId` on the wire; until then this
 * is the Map key the Phase 1 dual-write writes to and the components read from.
 */
export const SSH_PRIMARY_TERMINAL_ID = 'primary';

// ─── Transition table (R-A1) ────────────────────────────────────────────────────
//
// The canonical FSM, mirrored from the stateDiagram in
// voice-assistant/plans/ssh-terminal-fsm-migration.md § "1. Explicit FSM".
// `run-all.sh` (SE-SSH-FSM-COVERAGE) validates this row count against the number
// of simulator scenarios — adding a transition MUST add a scenario, and vice
// versa. Each `trigger` is a NAMED store action or a NAMED server event — never
// an "anything else" path (R-A2).

export const TRANSITIONS: Array<{ from: SshTerminalMode; to: SshTerminalMode; trigger: string }> = [
  { from: 'idle', to: 'picking', trigger: 'openTerminal' },
  { from: 'picking', to: 'connecting', trigger: 'selectTarget' },
  { from: 'connecting', to: 'connected', trigger: 'commitConnect' },
  { from: 'connecting', to: 'error', trigger: 'failConnect' },
  { from: 'connected', to: 'connected', trigger: 'appendOutput' },
  { from: 'connected', to: 'detached', trigger: 'markDetached' },
  { from: 'detached', to: 'connected', trigger: 'reconcile:attached' },
  { from: 'detached', to: 'idle', trigger: 'reconcile:gone' },
  { from: 'connected', to: 'disconnecting', trigger: 'exitTerminal' },
  { from: 'disconnecting', to: 'idle', trigger: 'onDisconnected' },
  { from: 'error', to: 'idle', trigger: 'dismissError' },
];

// ─── Map serializer ─────────────────────────────────────────────────────────────
// JSON.stringify/parse do not round-trip Map natively. These replacer/reviver
// functions let the persist middleware serialize the `terminals` Map. Mirrors
// composerStore.ts; covered by SE-SSH-STORE-SHAPE.

function mapReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Map) {
    return { __type: 'Map', entries: [...value.entries()] };
  }
  return value;
}

function mapReviver(_key: string, value: unknown): unknown {
  if (
    value !== null &&
    typeof value === 'object' &&
    (value as { __type?: string }).__type === 'Map'
  ) {
    return new Map((value as { entries: [unknown, unknown][] }).entries);
  }
  return value;
}

const mapAwareStorage = createJSONStorage(() => localStorage, {
  replacer: mapReplacer,
  reviver: mapReviver,
});

// ─── Store creation ─────────────────────────────────────────────────────────────

export const useSshTerminalStore = create<SshTerminalStoreShape>()(
  persist(
    (): SshTerminalStoreShape => ({
      terminals: new Map<string, SshTerminalState>(),
      targets: [],
    }),
    {
      name: 'va-ssh-terminal-v1',
      storage: mapAwareStorage,
      // Persist METADATA ONLY (mode/target/terminalId/voiceHistory). `output`
      // is stripped — it can be many KB and is re-sourced from the server PTY
      // buffer on reconcile (Phase 3). `targets` are ephemeral, never persisted.
      partialize: (state) => ({
        terminals: new Map(
          [...state.terminals.entries()].map(([id, t]) => [id, { ...t, output: [] as string[] }]),
        ),
        targets: [] as SshTarget[],
      }),
      // Server-authoritative rehydrate: on reload there is NO live socket, so any
      // persisted "live" mode is demoted rather than trusted (pattern rule 3 —
      // never synthesize server state). connected/detached → detached (resumable
      // once SSH_STATE arrives via reconcile, Phase 3); transient in-flight modes
      // → idle. This is inert in Phase 0 (no consumers) but keeps the scaffold
      // correct-by-construction so Phase 1 wiring inherits the right defaults.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const next = new Map<string, SshTerminalState>();
        for (const [id, t] of state.terminals.entries()) {
          let mode: SshTerminalMode = t.mode;
          if (t.mode === 'connected' || t.mode === 'detached') mode = 'detached';
          else if (t.mode === 'picking' || t.mode === 'connecting' || t.mode === 'disconnecting') mode = 'idle';
          next.set(id, { ...t, mode, output: [] });
        }
        useSshTerminalStore.setState({ terminals: next });
      },
    },
  ),
);

// ─── Internal helpers ────────────────────────────────────────────────────────────

/** Upsert a terminal entry through a transform, returning a fresh Map (immutable). */
function patchTerminal(
  id: string,
  patch: (cur: SshTerminalState) => SshTerminalState,
): void {
  useSshTerminalStore.setState((s) => {
    const terminals = new Map(s.terminals);
    const cur = terminals.get(id) ?? defaultSshTerminalState();
    terminals.set(id, patch(cur));
    return { terminals };
  });
}

// ─── Actions (one named action per transition / intent — R-A2/R-A4) ─────────────

/**
 * idle → picking. User opened the terminal surface; the picker shows targets.
 * The caller (Phase 1 wire) fetches SSH_TARGETS; setTargets() populates them.
 */
export function openTerminal(terminalId: string): void {
  patchTerminal(terminalId, (cur) => ({ ...cur, mode: 'picking', lastError: undefined }));
}

/** SSH_TARGETS handler — populate the picker list. Not a mode transition. */
export function setTargets(targets: SshTarget[]): void {
  useSshTerminalStore.setState({ targets });
}

/**
 * picking → connecting. User picked a target. The wire emits SSH_CONNECT.
 * Stores the chosen target so the connecting/connected UI can label itself.
 */
export function selectTarget(terminalId: string, target: SshTarget): void {
  patchTerminal(terminalId, (cur) => ({ ...cur, mode: 'connecting', target }));
}

/**
 * connecting → connected (SSH_CONNECTED, server). ATOMIC: sets mode + target +
 * server terminalId + clears any stale output from a prior session, in one
 * action (R-A4). This is the SSH analog of composer's commitSend.
 */
export function commitConnect(
  terminalId: string,
  target: SshTarget,
  serverTerminalId?: string,
): void {
  patchTerminal(terminalId, (cur) => ({
    ...cur,
    mode: 'connected',
    target,
    terminalId: serverTerminalId ?? cur.terminalId ?? terminalId,
    output: [], // clear stale output from a previous connection
    lastSeq: undefined, // fresh PTY — seq cursor restarts
    outputEpoch: cur.outputEpoch + 1, // output REPLACED (cleared) → renderer resets xterm
    connectedAt: Date.now(),
    lastActivityAt: Date.now(),
    lastError: undefined,
  }));
}

/** connecting → error (server ERROR / connect failure). */
export function failConnect(terminalId: string, message: string): void {
  patchTerminal(terminalId, (cur) => ({ ...cur, mode: 'error', lastError: message }));
}

/**
 * connected → connected (SSH_OUTPUT, server). Self-loop: append a PTY chunk and
 * bump last-activity. IDEMPOTENT on `seq` (Phase 4): a chunk whose seq is <= the
 * highest applied seq is DROPPED — this dedupes the reattach race (a live chunk
 * already captured in the replay buffer) deterministically. A chunk WITHOUT a seq
 * degrades to plain monotonic append (backward-compat). Does NOT bump outputEpoch
 * (append, not replace). (sendInput / resize are the user-side legs of this same
 * self-loop — they emit to the wire and do not mutate the store.)
 */
export function appendOutput(terminalId: string, chunk: string, seq?: number): void {
  patchTerminal(terminalId, (cur) => {
    if (seq !== undefined && cur.lastSeq !== undefined && seq <= cur.lastSeq) {
      return cur; // already applied (or stale duplicate) — drop, no re-append
    }
    return {
      ...cur,
      output: [...cur.output, chunk],
      lastSeq: seq !== undefined ? seq : cur.lastSeq,
      lastActivityAt: Date.now(),
    };
  });
}

/**
 * connected → detached (client observation of socket disconnect). The PTY is
 * kept ALIVE server-side — we keep `target` + `terminalId` so reconcile() can
 * resume. We do NOT clear output here and do NOT claim the PTY is dead.
 */
export function markDetached(terminalId: string): void {
  patchTerminal(terminalId, (cur) =>
    cur.mode === 'connected' ? { ...cur, mode: 'detached' } : cur,
  );
}

/**
 * detached → connected | idle (WsEvent.SSH_STATE, server reconcile). The SINGLE
 * server-authoritative truth signal for a detached terminal (pattern rule 3):
 *   attached:true  → resume the live terminal. When the server ships a `replay`
 *                    buffer it REPLACES the client's scrollback (server is the
 *                    source of truth) — this is the lost-history fix: reattach no
 *                    longer routes through SSH_CONNECTED→commitConnect (which
 *                    cleared output).
 *   attached:false → no live PTY; drop to idle and clear the entry's output.
 * One atomic action covering both `detached → connected` and `detached → idle`.
 * The attached:false branch is a no-op on an already-idle entry so a routine
 * "no terminal" handshake never churns the store.
 */
export function reconcile(sshState: SshStateReconcile): void {
  patchTerminal(sshState.terminalId, (cur) => {
    if (sshState.attached) {
      const replaced = sshState.replay !== undefined;
      return {
        ...cur,
        mode: 'connected',
        target: sshState.target ?? cur.target,
        terminalId: sshState.terminalId,
        output: replaced ? [sshState.replay as string] : cur.output,
        // Adopt the replay's high-water cursor so live chunks already in the
        // buffer (seq <= this) dedupe in appendOutput rather than double-append.
        lastSeq: sshState.seq !== undefined ? sshState.seq : cur.lastSeq,
        // A replay REPLACES scrollback → bump epoch so the renderer resets xterm
        // and re-sources from the buffer (the writtenRef append-only bug fix).
        outputEpoch: replaced ? cur.outputEpoch + 1 : cur.outputEpoch,
        lastActivityAt: Date.now(),
      };
    }
    // gone — back to idle, output cleared. Idle already ⇒ nothing to reconcile.
    return cur.mode === 'idle'
      ? cur
      : { ...defaultSshTerminalState(), outputEpoch: cur.outputEpoch + 1 };
  });
}

/**
 * connected → disconnecting (VOLUNTARY leave — back arrow OR keyboard Exit).
 * The single atomic intent that promotes the PR #1371 back-arrow band-aid: it
 * transitions out of `connected` so the surface gate (`sshConnected`) clears and
 * opening another chat renders chat, not a stranded terminal. The wire emits
 * SSH_DISCONNECT; SSH_DISCONNECTED then drives disconnecting → idle.
 * Both the back arrow (App.tsx) and the keyboard Exit (SshToolbar.tsx) call THIS
 * one action (Phase 2 wiring) — never two different code paths.
 */
export function exitTerminal(terminalId: string): void {
  patchTerminal(terminalId, (cur) =>
    cur.mode === 'connected' ? { ...cur, mode: 'disconnecting' } : cur,
  );
}

/** disconnecting → idle (SSH_DISCONNECTED, server). Clears the entry (epoch bump
 *  so the renderer resets xterm on the next connect). */
export function onDisconnected(terminalId: string): void {
  patchTerminal(terminalId, (cur) => ({
    ...defaultSshTerminalState(),
    outputEpoch: cur.outputEpoch + 1,
  }));
}

/** error → idle (user dismiss). */
export function dismissError(terminalId: string): void {
  patchTerminal(terminalId, (cur) =>
    cur.mode === 'error'
      ? { ...defaultSshTerminalState(), outputEpoch: cur.outputEpoch + 1 }
      : cur,
  );
}

/** Append a voice-transcribed command (FIFO cap). Not a mode transition. */
export function appendVoiceHistory(terminalId: string, command: string): void {
  patchTerminal(terminalId, (cur) => ({
    ...cur,
    voiceHistory: [...cur.voiceHistory, command].slice(-VOICE_HISTORY_CAP),
  }));
}

// ─── Non-hook accessors (for effect bodies / event handlers) ────────────────────

export function getSshTerminal(
  state: SshTerminalStoreShape,
  terminalId: string | null,
): SshTerminalState | undefined {
  if (!terminalId) return undefined;
  return state.terminals.get(terminalId);
}

export function getSshMode(
  state: SshTerminalStoreShape,
  terminalId: string | null,
): SshTerminalMode {
  if (!terminalId) return 'idle';
  return state.terminals.get(terminalId)?.mode ?? 'idle';
}

// ─── Hook selectors — the ONLY supported way to read terminal state in React ────

/** Subscribe to a single terminal's FSM mode. */
export function useSshMode(terminalId: string | null): SshTerminalMode {
  return useSshTerminalStore(
    useShallow((s) => (terminalId ? s.terminals.get(terminalId)?.mode ?? 'idle' : 'idle')),
  );
}

/** Subscribe to a single terminal's output chunks. */
export function useSshOutput(terminalId: string | null): string[] {
  return useSshTerminalStore(
    useShallow((s) => (terminalId ? s.terminals.get(terminalId)?.output ?? [] : [])),
  );
}

/** Subscribe to a terminal's output epoch (Phase 4). Changes ⇒ output was
 *  REPLACED (commitConnect/reconcile/reset), not appended — the SshTerminal
 *  renderer resets xterm and re-sources, otherwise it writes the append delta. */
export function useSshOutputEpoch(terminalId: string | null): number {
  return useSshTerminalStore(
    (s) => (terminalId ? s.terminals.get(terminalId)?.outputEpoch ?? 0 : 0),
  );
}

/** Subscribe to a single terminal's connected/selected target (null when none). */
export function useSshTarget(terminalId: string | null): SshTarget | null {
  return useSshTerminalStore(
    (s) => (terminalId ? s.terminals.get(terminalId)?.target ?? null : null),
  );
}

/** Subscribe to the available SSH targets (picker). */
export function useSshTargets(): SshTarget[] {
  return useSshTerminalStore(useShallow((s) => s.targets));
}

/** Subscribe to a single terminal's voice-command history (SSH PTT chips). */
export function useSshVoiceHistory(terminalId: string | null): string[] {
  return useSshTerminalStore(
    useShallow((s) => (terminalId ? s.terminals.get(terminalId)?.voiceHistory ?? [] : [])),
  );
}

/** True iff the given terminal is in a live/active mode (connected or in-flight). */
export function useSshActive(terminalId: string | null): boolean {
  const mode = useSshMode(terminalId);
  return mode === 'connecting' || mode === 'connected' || mode === 'disconnecting';
}
